import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, TextField, Grid,
  Card, CardContent, Avatar, Tab, Tabs, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, Snackbar, Alert, CardMedia
} from "@mui/material";
import {
  Visibility, Search, Refresh, Business, PostAdd, Category, Subscriptions, Add, Edit, Delete, Image as ImageIcon
} from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../utils/api-urls";

const API_BASE = api_urls + "api/postbanao/admin";

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return "https://via.placeholder.com/300x200?text=Template";
  if (url.startsWith("http")) return url;
  return "https://api.bharatdarshan.app" + url;
};


const PostBanaoDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  const [stats, setStats] = useState({ totalProfiles: 0, totalTemplates: 0, totalPacks: 0, activeSubscriptions: 0, totalCategories: 0 });
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  
  const [templateDialog, setTemplateDialog] = useState({ open: false, mode: "add", data: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: "add", data: null });
  const [packDialog, setPackDialog] = useState({ open: false, mode: "add", data: null });
  const [planDialog, setPlanDialog] = useState({ open: false, mode: "add", data: null });
  
  const fileInputRef = useRef(null);

  useEffect(() => { fetchStats(); fetchCategories(); }, []);
  useEffect(() => { fetchData(); }, [tabValue, page, rowsPerPage]);

  const showMessage = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/stats`);
      if (data?.success) setStats(data.data);
    } catch (e) { console.error(e); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/categories`);
      if (data?.success) setCategories(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const endpoints = ["profiles", "templates", "categories", "packs", "plans"];
      const { data } = await axios.get(`${API_BASE}/${endpoints[tabValue]}?page=${page + 1}&limit=${rowsPerPage}${searchText ? "&search=" + searchText : ""}`);
      if (data?.success) {
        [setProfiles, setTemplates, setCategories, setPacks, setPlans][tabValue](data.data || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSaveTemplate = async () => {
    try {
      const { mode, data } = templateDialog;
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === "imageFile" && data[key]) formData.append("image", data[key]);
        else if (data[key] !== undefined && data[key] !== null) formData.append(key, data[key]);
      });
      if (mode === "add") await axios.post(`${API_BASE}/templates`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      else await axios.put(`${API_BASE}/templates/${data._id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      showMessage(`Template ${mode === "add" ? "created" : "updated"}!`);
      setTemplateDialog({ open: false, mode: "add", data: null });
      fetchData(); fetchStats();
    } catch (e) { showMessage(e.response?.data?.message || "Error", "error"); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API_BASE}/templates/${id}`); showMessage("Deleted!"); fetchData(); fetchStats(); }
    catch (e) { showMessage("Error", "error"); }
  };

  const handleSaveCategory = async () => {
    try {
      const { mode, data } = categoryDialog;
      if (mode === "add") await axios.post(`${API_BASE}/categories`, data);
      else await axios.put(`${API_BASE}/categories/${data._id}`, data);
      showMessage(`Category ${mode === "add" ? "created" : "updated"}!`);
      setCategoryDialog({ open: false, mode: "add", data: null });
      fetchCategories(); fetchData(); fetchStats();
    } catch (e) { showMessage(e.response?.data?.message || "Error", "error"); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API_BASE}/categories/${id}`); showMessage("Deleted!"); fetchCategories(); fetchData(); }
    catch (e) { showMessage("Error", "error"); }
  };

  const handleSavePack = async () => {
    try {
      const { mode, data } = packDialog;
      if (mode === "add") await axios.post(`${API_BASE}/packs`, data);
      else await axios.put(`${API_BASE}/packs/${data._id}`, data);
      showMessage(`Pack ${mode === "add" ? "created" : "updated"}!`);
      setPackDialog({ open: false, mode: "add", data: null });
      fetchData();
    } catch (e) { showMessage(e.response?.data?.message || "Error", "error"); }
  };

  const handleSavePlan = async () => {
    try {
      const { mode, data } = planDialog;
      if (mode === "add") await axios.post(`${API_BASE}/plans`, data);
      else await axios.put(`${API_BASE}/plans/${data._id}`, data);
      showMessage(`Plan ${mode === "add" ? "created" : "updated"}!`);
      setPlanDialog({ open: false, mode: "add", data: null });
      fetchData();
    } catch (e) { showMessage(e.response?.data?.message || "Error", "error"); }
  };

  const renderStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[
        { label: "Business Profiles", value: stats.totalProfiles, icon: <Business />, color: "#2196F3" },
        { label: "Templates", value: stats.totalTemplates, icon: <ImageIcon />, color: "#4CAF50" },
        { label: "Categories", value: stats.totalCategories || categories.length, icon: <Category />, color: "#9C27B0" },
        { label: "Packs", value: stats.totalPacks, icon: <PostAdd />, color: "#FF9800" },
        { label: "Active Subscriptions", value: stats.activeSubscriptions, icon: <Subscriptions />, color: "#E91E63" },
      ].map((stat, i) => (
        <Grid item xs={6} md={2.4} key={i}>
          <Card sx={{ background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)` }}>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Avatar sx={{ bgcolor: stat.color, mx: "auto", mb: 1 }}>{stat.icon}</Avatar>
              <Typography variant="h4" fontWeight="bold">{stat.value}</Typography>
              <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderProfiles = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
          <TableCell>Business Name</TableCell><TableCell>Category</TableCell>
          <TableCell>Phone</TableCell><TableCell>City</TableCell><TableCell>Created</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {profiles.map((p) => (
            <TableRow key={p._id}>
              <TableCell><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar src={p.logoUrl} sx={{ width: 40, height: 40 }}>{p.businessName?.[0]}</Avatar>
                <Typography fontWeight="500">{p.businessName}</Typography>
              </Box></TableCell>
              <TableCell>{p.category}</TableCell>
              <TableCell>{p.phone}</TableCell>
              <TableCell>{p.city}</TableCell>
              <TableCell>{new Date(p.createdAt).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTemplates = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setTemplateDialog({ open: true, mode: "add", data: { name: "", nameHi: "", category: "", isActive: true, showInSlider: false, isPremium: false, dayNumber: 1 } })}>Add Template</Button>
      </Box>
      <Grid container spacing={2}>
        {templates.map((t) => (
          <Grid item xs={12} sm={6} md={3} key={t._id}>
            <Card sx={{ position: "relative" }}>
              {t.showInSlider && <Chip label="SLIDER" size="small" color="primary" sx={{ position: "absolute", top: 8, left: 8, zIndex: 1 }} />}
              {t.isPremium && <Chip label="PREMIUM" size="small" color="secondary" sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }} />}
              <CardMedia component="img" height="160" image={getImageUrl(t.imageUrl || t.thumbnailUrl)} alt={t.name} />
              <CardContent>
                <Typography fontWeight="600" noWrap>{t.name}</Typography>
                <Typography variant="body2" color="text.secondary">{t.nameHi}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                  <Chip label={t.category} size="small" />
                  <Chip label={`Day ${t.dayNumber}`} size="small" variant="outlined" />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <IconButton size="small" onClick={() => setTemplateDialog({ open: true, mode: "edit", data: t })}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(t._id)}><Delete /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  const renderCategories = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCategoryDialog({ open: true, mode: "add", data: { name: "", nameHi: "", icon: "🎨", order: categories.length + 1, isActive: true } })}>Add Category</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell>Order</TableCell><TableCell>Icon</TableCell><TableCell>Name (EN)</TableCell>
            <TableCell>Name (HI)</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {categories.sort((a, b) => a.order - b.order).map((c) => (
              <TableRow key={c._id}>
                <TableCell><Chip label={c.order} size="small" /></TableCell>
                <TableCell sx={{ fontSize: 24 }}>{c.icon}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.nameHi}</TableCell>
                <TableCell><Chip label={c.isActive ? "Active" : "Inactive"} color={c.isActive ? "success" : "default"} size="small" /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setCategoryDialog({ open: true, mode: "edit", data: c })}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteCategory(c._id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderPacks = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setPackDialog({ open: true, mode: "add", data: { dayNumber: packs.length + 1, title: "", titleHi: "", topic: "", description: "", tips: [], isActive: true } })}>Add Pack</Button>
      </Box>
      <Grid container spacing={2}>
        {packs.sort((a, b) => a.dayNumber - b.dayNumber).map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Chip label={`Day ${p.dayNumber}`} color="primary" />
                  <Chip label={p.isActive ? "Active" : "Inactive"} color={p.isActive ? "success" : "default"} size="small" />
                </Box>
                <Typography variant="h6" sx={{ mt: 1 }}>{p.title}</Typography>
                <Typography variant="body2" color="text.secondary">{p.titleHi}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}><strong>Topic:</strong> {p.topic}</Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <IconButton size="small" onClick={() => setPackDialog({ open: true, mode: "edit", data: p })}><Edit /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  const renderPlans = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setPlanDialog({ open: true, mode: "add", data: { name: "", nameHi: "", price: 0, originalPrice: 0, duration: 30, features: [], isActive: true, isPopular: false } })}>Add Plan</Button>
      </Box>
      <Grid container spacing={2}>
        {plans.map((p) => (
          <Grid item xs={12} sm={6} md={3} key={p._id}>
            <Card sx={{ border: p.isPopular ? "2px solid #FF6B00" : "none", position: "relative" }}>
              {p.isPopular && <Chip label="POPULAR" color="warning" size="small" sx={{ position: "absolute", top: 8, right: 8 }} />}
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h6">{p.name}</Typography>
                <Typography variant="body2" color="text.secondary">{p.nameHi}</Typography>
                <Typography variant="h4" sx={{ my: 2 }}>₹{p.price}</Typography>
                {p.originalPrice > p.price && <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>₹{p.originalPrice}</Typography>}
                <Typography variant="body2" color="primary">{p.duration} Days</Typography>
                <Chip label={p.isActive ? "Active" : "Inactive"} color={p.isActive ? "success" : "default"} size="small" sx={{ mt: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <IconButton onClick={() => setPlanDialog({ open: true, mode: "edit", data: p })}><Edit /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>📱 Post Banao - Admin</Typography>
      {renderStats()}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); setPage(0); }} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Business />} label="Profiles" />
          <Tab icon={<ImageIcon />} label="Templates" />
          <Tab icon={<Category />} label="Categories" />
          <Tab icon={<PostAdd />} label="Packs" />
          <Tab icon={<Subscriptions />} label="Plans" />
        </Tabs>
      </Paper>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField size="small" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ width: 300 }} />
        <IconButton onClick={fetchData}><Refresh /></IconButton>
      </Box>
      {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box> : (
        <>{tabValue === 0 && renderProfiles()}{tabValue === 1 && renderTemplates()}{tabValue === 2 && renderCategories()}{tabValue === 3 && renderPacks()}{tabValue === 4 && renderPlans()}</>
      )}
      <TablePagination component="div" count={100} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
      
      <Dialog open={templateDialog.open} onClose={() => setTemplateDialog({ ...templateDialog, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{templateDialog.mode === "add" ? "Add Template" : "Edit Template"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (English)" value={templateDialog.data?.name || ""} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, name: e.target.value } })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (Hindi)" value={templateDialog.data?.nameHi || ""} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, nameHi: e.target.value } })} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Category</InputLabel>
                <Select value={templateDialog.data?.category || ""} label="Category" onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, category: e.target.value } })}>
                  {categories.map((c) => <MenuItem key={c._id} value={c.name}>{c.icon} {c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Day Number" value={templateDialog.data?.dayNumber || 1} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, dayNumber: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={12}>
              <Box sx={{ border: "2px dashed #ccc", borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer" }} onClick={() => fileInputRef.current?.click()}>
                {templateDialog.data?.imagePreview || templateDialog.data?.imageUrl ? <img src={templateDialog.data?.imagePreview || getImageUrl(templateDialog.data?.imageUrl)} alt="Preview" style={{ maxHeight: 200, maxWidth: "100%" }} /> : <><ImageIcon sx={{ fontSize: 48, color: "#ccc" }} /><Typography color="text.secondary">Click to upload</Typography></>}
              </Box>
              <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={(e) => { const file = e.target.files[0]; if (file) setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, imageFile: file, imagePreview: URL.createObjectURL(file) } }); }} />
            </Grid>
            <Grid item xs={4}><FormControlLabel control={<Switch checked={templateDialog.data?.isActive || false} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, isActive: e.target.checked } })} />} label="Active" /></Grid>
            <Grid item xs={4}><FormControlLabel control={<Switch checked={templateDialog.data?.showInSlider || false} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, showInSlider: e.target.checked } })} />} label="Show in Slider" /></Grid>
            <Grid item xs={4}><FormControlLabel control={<Switch checked={templateDialog.data?.isPremium || false} onChange={(e) => setTemplateDialog({ ...templateDialog, data: { ...templateDialog.data, isPremium: e.target.checked } })} />} label="Premium" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setTemplateDialog({ ...templateDialog, open: false })}>Cancel</Button><Button variant="contained" onClick={handleSaveTemplate}>Save</Button></DialogActions>
      </Dialog>
      
      <Dialog open={categoryDialog.open} onClose={() => setCategoryDialog({ ...categoryDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{categoryDialog.mode === "add" ? "Add Category" : "Edit Category"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (English)" value={categoryDialog.data?.name || ""} onChange={(e) => setCategoryDialog({ ...categoryDialog, data: { ...categoryDialog.data, name: e.target.value } })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (Hindi)" value={categoryDialog.data?.nameHi || ""} onChange={(e) => setCategoryDialog({ ...categoryDialog, data: { ...categoryDialog.data, nameHi: e.target.value } })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Icon (Emoji)" value={categoryDialog.data?.icon || ""} onChange={(e) => setCategoryDialog({ ...categoryDialog, data: { ...categoryDialog.data, icon: e.target.value } })} placeholder="🎨" /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Order" value={categoryDialog.data?.order || 1} onChange={(e) => setCategoryDialog({ ...categoryDialog, data: { ...categoryDialog.data, order: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={categoryDialog.data?.isActive || false} onChange={(e) => setCategoryDialog({ ...categoryDialog, data: { ...categoryDialog.data, isActive: e.target.checked } })} />} label="Active" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setCategoryDialog({ ...categoryDialog, open: false })}>Cancel</Button><Button variant="contained" onClick={handleSaveCategory}>Save</Button></DialogActions>
      </Dialog>
      
      <Dialog open={packDialog.open} onClose={() => setPackDialog({ ...packDialog, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{packDialog.mode === "add" ? "Add Pack" : "Edit Pack"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Day Number" value={packDialog.data?.dayNumber || 1} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, dayNumber: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Title (English)" value={packDialog.data?.title || ""} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, title: e.target.value } })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Title (Hindi)" value={packDialog.data?.titleHi || ""} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, titleHi: e.target.value } })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Topic" value={packDialog.data?.topic || ""} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, topic: e.target.value } })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Description" value={packDialog.data?.description || ""} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, description: e.target.value } })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Tips (one per line)" value={(packDialog.data?.tips || []).join("\n")} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, tips: e.target.value.split("\n").filter(t => t.trim()) } })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={packDialog.data?.isActive || false} onChange={(e) => setPackDialog({ ...packDialog, data: { ...packDialog.data, isActive: e.target.checked } })} />} label="Active" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setPackDialog({ ...packDialog, open: false })}>Cancel</Button><Button variant="contained" onClick={handleSavePack}>Save</Button></DialogActions>
      </Dialog>
      
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ ...planDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{planDialog.mode === "add" ? "Add Plan" : "Edit Plan"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (English)" value={planDialog.data?.name || ""} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, name: e.target.value } })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name (Hindi)" value={planDialog.data?.nameHi || ""} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, nameHi: e.target.value } })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Price" value={planDialog.data?.price || 0} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, price: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Original Price" value={planDialog.data?.originalPrice || 0} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, originalPrice: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Duration (Days)" value={planDialog.data?.duration || 30} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, duration: parseInt(e.target.value) } })} /></Grid>
            <Grid item xs={6}><FormControlLabel control={<Switch checked={planDialog.data?.isPopular || false} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, isPopular: e.target.checked } })} />} label="Popular" /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Features (one per line)" value={(planDialog.data?.features || []).join("\n")} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, features: e.target.value.split("\n").filter(f => f.trim()) } })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={planDialog.data?.isActive || false} onChange={(e) => setPlanDialog({ ...planDialog, data: { ...planDialog.data, isActive: e.target.checked } })} />} label="Active" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setPlanDialog({ ...planDialog, open: false })}>Cancel</Button><Button variant="contained" onClick={handleSavePlan}>Save</Button></DialogActions>
      </Dialog>
      
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
};

export default PostBanaoDashboard;
